import React, { useState } from 'react';
import { presetsByPlatform, presetById, maxDurationLabel } from '../utils/social';
import { requestSocialExport, deleteExport } from '../data/firestore';
import { httpsify } from '../utils/url';
import { renderEtaLabel, useNow } from '../utils/eta';

// Friendly status verb for an export job.
function exportLabel(job, now) {
  const r = { status: job.status, progress: job.progress, startedAt: job.startedAt };
  return renderEtaLabel(r, now, 'Exporting');
}

// A miniature of the target frame shape, drawn at the preset's aspect ratio.
function RatioFrame({ w, h }) {
  const portrait = h >= w;
  const bw = portrait ? Math.round((w / h) * 34) : 46;
  const bh = portrait ? 34 : Math.round((h / w) * 46);
  return (
    <span className="cs-fmt-frame" aria-hidden>
      <span style={{ width: bw, height: bh }} />
    </span>
  );
}

export default function SocialExportPanel({
  movieId,
  movieTitle,
  subjectTrackId,
  waypoints,
  cropScale = 1,
  cropSegments,
  exports,
  previewAspect,
  onFocusAspect,
  movieFps = 30,
  hasExportMaster = false,
}) {
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  // Cap the chooser to what the master can actually deliver (you can't conjure
  // 60fps from a 30fps timeline). 60 only offered when the movie renders >=48fps.
  const canDo60 = Number(movieFps) >= 48;
  const [fps, setFps] = useState(() => (canDo60 ? 60 : 30));
  const [sharpen, setSharpen] = useState(false);
  const groups = presetsByPlatform();
  const anyActive = (exports || []).some((e) => e.status === 'queued' || e.status === 'rendering');
  const now = useNow(anyActive);

  const toggle = (p) => {
    onFocusAspect?.(p.aspect);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p.id)) next.delete(p.id);
      else next.add(p.id);
      return next;
    });
  };

  const startExport = async () => {
    if (!selected.size) return;
    setBusy(true);
    try {
      await requestSocialExport(movieId, {
        presets: Array.from(selected),
        waypoints: waypoints || [],
        subjectTrackId: subjectTrackId || null,
        cropScale: cropScale || 1,
        cropSegments: Array.isArray(cropSegments) ? cropSegments : null,
        label: movieTitle || '',
        fps: canDo60 ? fps : 0,
        sharpen: sharpen ? 0.6 : 0,
      });
      setSelected(new Set());
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p className="cs-help" style={{ marginBottom: 10 }}>
        Pick the exact formats each network supports. Selecting one previews its crop on the right — tick every format
        you want and export them all at once.
      </p>
      <div className="cs-fmt-cols">
        {groups.map((g) => (
          <div key={g.platform} className="cs-fmt-group">
            <div className="cs-fmt-platform">{g.platform}</div>
            {g.presets.map((p) => {
              const on = selected.has(p.id);
              const previewing = previewAspect && p.aspect === previewAspect;
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`cs-fmt-card ${on ? 'on' : ''} ${previewing ? 'previewing' : ''}`}
                  onClick={() => toggle(p)}
                  title={p.note}
                >
                  <RatioFrame w={p.w} h={p.h} />
                  <span className="cs-fmt-body">
                    <span className="cs-fmt-title">
                      {p.label}
                      {p.recommended && <span className="cs-fmt-star" title="Recommended">★</span>}
                      {previewing && <span className="cs-fmt-eye" title="Previewing this crop">👁</span>}
                    </span>
                    <span className="cs-fmt-meta">
                      {p.placement} · {p.aspect.replace('x', ':')} · {p.w}×{p.h} · {maxDurationLabel(p.maxSec)}
                    </span>
                    <span className="cs-fmt-note">{p.note}</span>
                  </span>
                  <span className={`cs-fmt-check ${on ? 'on' : ''}`}>{on ? '✓' : ''}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <span style={{ color: 'var(--muted)' }}>Frame rate</span>
          <select
            className="cs-input"
            style={{ width: 'auto', padding: '4px 8px' }}
            value={canDo60 ? fps : 30}
            disabled={!canDo60}
            onChange={(e) => setFps(Number(e.target.value))}
            title={canDo60 ? 'Reels frame rate' : 'Publish/render the movie at 48–60fps to unlock 60fps exports'}
          >
            <option value={30}>30 fps</option>
            {canDo60 && <option value={60}>60 fps (smooth action)</option>}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={sharpen} onChange={(e) => setSharpen(e.target.checked)} />
          <span>Output sharpen</span>
        </label>
      </div>
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button className="cs-btn cs-btn-primary" disabled={!selected.size || busy} onClick={startExport}>
          {busy ? 'Queuing…' : `Export ${selected.size || ''} format${selected.size === 1 ? '' : 's'}`.trim()}
        </button>
        <span className="cs-help">
          {hasExportMaster
            ? 'Cropped from the high-res RED export master (no upscaling). '
            : 'Tip: publish the movie first (ideally from RAW) so exports crop from the high-res RED master instead of the 1080p preview. '}
          Encoding runs on the backend — you can leave this page.
        </span>
      </div>

      {/* Export jobs */}
      <div style={{ marginTop: 22 }}>
        <h3 style={{ fontSize: 16, marginBottom: 8 }}>Exports</h3>
        {(exports || []).length === 0 ? (
          <div className="cs-empty">No exports yet. Pick formats above and hit Export.</div>
        ) : (
          (exports || []).map((job) => (
            <div key={job.id} className="cs-export-job">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="cs-badge">{job.status}</span>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  {(job.presets || []).map((p) => presetById(p)?.label || p).join(' · ')}
                </span>
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => deleteExport(job.id)}
                >
                  Remove
                </button>
              </div>
              {(job.status === 'queued' || job.status === 'rendering') && (
                <div style={{ marginTop: 8 }}>
                  <div className="cs-help">{exportLabel(job, now)}</div>
                  <div className="cs-progress" style={{ marginTop: 6 }}>
                    <div style={{ width: `${job.progress || 3}%` }} />
                  </div>
                </div>
              )}
              {job.status === 'error' && (
                <div className="cs-empty" style={{ marginTop: 8 }}>Failed: {job.message || 'unknown error'}</div>
              )}
              {(job.outputs || []).length > 0 && (
                <div className="cs-export-outs">
                  {job.outputs.map((o) => (
                    <a
                      key={o.presetId}
                      className="cs-btn cs-btn-ghost cs-btn-sm"
                      href={httpsify(o.url)}
                      download
                      target="_blank"
                      rel="noreferrer"
                    >
                      ⬇ {o.label || o.presetId} ({o.w}×{o.h}{o.fps ? `·${o.fps}p` : ''})
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
